/**
 * Recognising call links in a message body.
 *
 * A call is an ordinary message — no new message type, no schema change, nothing for the backend
 * to learn — so the only thing that makes one render as a call card is the link inside it. That
 * also means a call link somebody pastes by hand gets the same card, which is the right outcome,
 * and it is why this recognises every provider people actually paste rather than only the one the
 * call button happens to mint.
 */

export type ChatCallProvider = 'meet' | 'zoom' | 'teams' | 'webex' | 'whereby' | 'jitsi';

/** A call link found in a message body, with everything the card needs to describe it. */
export interface ChatCallLink {
  /** What the Join button opens, with trailing sentence punctuation removed. */
  url: string;
  provider: ChatCallProvider;
  /** "Google Meet", "Zoom", … — what the card calls it. */
  providerName: string;
  /** The room code, or the host when there isn't a human-readable one. Purely cosmetic. */
  label: string;
  /** True when the link is a lobby rather than a shared room, so the copy can say so. */
  instant: boolean;
}

interface ProviderSpec {
  id: ChatCallProvider;
  name: string;
  pattern: RegExp;
}

/**
 * One pattern per provider, deliberately narrower than "any URL on their domain".
 *
 * Meet, Jitsi and Whereby only ever serve rooms, so their whole domain is fair game. Zoom,
 * Teams and Webex also host marketing and downloads, and a link to a pricing page rendering as
 * "Sarah started a video call" would be worse than not recognising it at all — so those require
 * the path segment their join links actually carry.
 */
const PROVIDERS: ProviderSpec[] = [
  { id: 'meet', name: 'Google Meet', pattern: /https?:\/\/(?:meet\.google\.com|g\.co\/meet)\/[^\s<]+/i },
  { id: 'zoom', name: 'Zoom', pattern: /https?:\/\/(?:[\w-]+\.)?zoom\.us\/(?:j|s|w|wc|my)\/[^\s<]+/i },
  {
    id: 'teams',
    name: 'Microsoft Teams',
    pattern: /https?:\/\/teams\.microsoft\.com\/l\/meetup-join\/[^\s<]+|https?:\/\/teams\.live\.com\/meet\/[^\s<]+/i,
  },
  { id: 'webex', name: 'Webex', pattern: /https?:\/\/(?:[\w-]+\.)?webex\.com\/[^\s<]*(?:meet|join|j\.php)[^\s<]*/i },
  { id: 'whereby', name: 'Whereby', pattern: /https?:\/\/(?:www\.)?whereby\.com\/[^\s<]+/i },
  { id: 'jitsi', name: 'Jitsi Meet', pattern: /https?:\/\/meet\.jit\.si\/[^\s<]+/i },
];

/**
 * Trailing punctuation is part of the sentence, not the URL — "join at <link>." must not 404.
 *
 * The same set `chat-link.util.ts` trims, quotes and braces included: a link is as likely to be
 * quoted or wrapped in a JSON payload as it is to end a sentence, and neither character has ever
 * been part of a URL somebody meant to share.
 */
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/;

/**
 * Path segments that are routing, not the room: `zoom.us/j/<id>`, `g.co/meet/<nickname>`,
 * `teams.microsoft.com/l/meetup-join/<blob>` all bury the interesting part one level down.
 */
const ROUTING_SEGMENTS = ['meet', 'meetup-join', 'join', 'j', 'my', 'w', 'wc', 'l', 's'];

/** The longest room code worth printing; past this it is an opaque blob and the host reads better. */
const MAX_LABEL_LENGTH = 24;

/**
 * The first call link in a message body, or null.
 *
 * "First" is by position in the text rather than by provider order, so a body quoting two links
 * describes the one the reader sees first.
 */
export function extractCallLink(body: string | null | undefined): ChatCallLink | null {
  const text = body || '';
  if (!text) {
    return null;
  }

  let best: { spec: ProviderSpec; url: string; at: number } | null = null;

  for (const spec of PROVIDERS) {
    const match = text.match(spec.pattern);
    const at = match?.index;
    if (!match || at === undefined) {
      continue;
    }
    if (!best || at < best.at) {
      best = { spec, url: match[0].replace(TRAILING_PUNCTUATION, ''), at };
    }
  }

  if (!best) {
    return null;
  }

  return {
    url: best.url,
    provider: best.spec.id,
    providerName: best.spec.name,
    label: roomLabel(best.url),
    instant: isInstantMeetLink(best.url),
  };
}

/** True when a body carries a call link at all — cheaper to read at a call site than a null check. */
export function hasCallLink(body: string | null | undefined): boolean {
  return !!extractCallLink(body);
}

/**
 * The marker the call button puts at the front of its message.
 *
 * Exported so the store composes the body from it and {@link isCallStartedMessage} tests for it —
 * two places reading one constant, rather than a string that can drift out of step with the check
 * that depends on it.
 */
export const CHAT_CALL_STARTED_MARKER = '📹';

/**
 * The marker on a message re-sharing a call that is already running.
 *
 * Distinct from {@link CHAT_CALL_STARTED_MARKER} because the two are not the same event and the
 * card must not say otherwise: whoever presses the button while Sarah's call is up did not start
 * it, and a bubble claiming they did is simply false.
 */
export const CHAT_CALL_RESHARE_MARKER = '📣';

/**
 * True when this message is somebody starting a call *now*, rather than sharing a link.
 *
 * The distinction is the whole basis for whether we ring. Both render as a call card — a pasted
 * Zoom link should still get a Join button — but "here's the link for Thursday's inspection
 * briefing" must not make six phones ring on a Tuesday. Only a call started from Remmi's own
 * button carries the marker, which makes this precise rather than a guess about intent.
 */
export function isCallStartedMessage(body: string | null | undefined): boolean {
  return (body || '').trimStart().startsWith(CHAT_CALL_STARTED_MARKER) && hasCallLink(body);
}

/**
 * True when this message announces a call — started or re-shared. This is what rings.
 *
 * A re-share rings for the same reason the original did: it is somebody saying "we are on a call
 * now, come in". Anything without a marker stays silent, so a pasted link is still just a link.
 */
export function isCallAnnouncement(body: string | null | undefined): boolean {
  const text = (body || '').trimStart();
  return (text.startsWith(CHAT_CALL_STARTED_MARKER) || text.startsWith(CHAT_CALL_RESHARE_MARKER)) && hasCallLink(body);
}

/**
 * True when a piece of text is *about* a call, link or no link.
 *
 * Deliberately looser than {@link isCallStartedMessage}: this reads sidebar previews, which the
 * server truncates, so the URL is frequently not there to match. Ringing has to be certain and
 * uses the strict check; deciding whether a thread belongs in the Calls list does not.
 */
export function looksLikeCall(text: string | null | undefined): boolean {
  const trimmed = (text || '').trimStart();
  return trimmed.startsWith(CHAT_CALL_STARTED_MARKER) || trimmed.startsWith(CHAT_CALL_RESHARE_MARKER) || hasCallLink(text);
}

/**
 * A Google Meet link specifically.
 *
 * Kept separate from {@link extractCallLink} because its one caller is walking a Google Calendar
 * response for the room Google just minted, where matching any other provider would be a bug.
 */
export function extractMeetLink(body: string | null | undefined): string | null {
  const match = (body || '').match(PROVIDERS[0].pattern);
  return match ? match[0].replace(TRAILING_PUNCTUATION, '') : null;
}

/**
 * True when the link is Meet's "start a new meeting" lobby rather than a specific room.
 *
 * Nothing in Remmi posts one of these any more — a call either mints a real room or fails and
 * says so, because `/new` sends every person who taps it into a *separate* meeting. This survives
 * for two reasons: threads still hold old messages that carry one, and somebody can always paste
 * it by hand. Both need the card to say "instant meeting" rather than imply a shared room.
 */
export function isInstantMeetLink(url: string): boolean {
  return /meet\.google\.com\/new\b/i.test(url);
}

/**
 * The room code shown on the card: `abc-defg-hij`, `remmi-sales`, a Zoom meeting number.
 *
 * Falls back to the host when what's left is an opaque blob — a Teams join URL is several hundred
 * percent-encoded characters, and "teams.microsoft.com" tells the reader more than the first
 * twenty of them would.
 */
export function roomLabel(url: string): string {
  const withoutScheme = url.replace(/^https?:\/\//i, '').replace(/[?#].*$/, '');
  const [host, ...segments] = withoutScheme.split('/').filter(Boolean);
  const room = segments.find(segment => !ROUTING_SEGMENTS.includes(segment.toLowerCase()));

  if (!room || room.length > MAX_LABEL_LENGTH || room.includes('%')) {
    return host ?? withoutScheme;
  }
  return room;
}

/**
 * A "add this call to my calendar" link.
 *
 * Google Calendar's public template URL, which needs no API, no connected account and no consent
 * screen — it opens a pre-filled event the user saves themselves. That is the whole reason to use
 * it over the Calendar API the call button uses: this action has to work for the people *receiving*
 * a call link, who may never have connected Google at all.
 */
export function calendarTemplateUrl(url: string, title: string, start: Date = new Date(), minutes = 30): string {
  const end = new Date(start.getTime() + minutes * 60 * 1000);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${calendarStamp(start)}/${calendarStamp(end)}`,
    details: `Join the call: ${url}`,
    location: url,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** `20260804T051500Z` — the basic-format UTC stamp the template URL expects. */
function calendarStamp(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}
